import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, IconButton,
  List, ListItem, ListItemButton, Avatar, Chip,
  Tab, Tabs, CircularProgress, Stack, Divider, InputAdornment,
} from '@mui/material';
import { Close, Search, PersonAdd, Phone, Email, AccountBalanceWallet, ArrowBack, Visibility, ReceiptLong, LocationOn, Stars, Cake, CardGiftcard, ContentCopy } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useLazySearchCustomersQuery, useCreateCustomerMutation, useGetCustomerQuery, useEnrollLoyaltyMutation, useActivateCustomerMutation } from '../../services/customerApi';
import { useUpdateOrderMutation, useRemoveLoyaltyMutation } from '../../services/orderApi';
import { setSelectedCustomer, setCurrentOrder } from '../../store/orderSlice';
import { useToast } from '../common/ToastProvider';

const fmtPts = (v) => Math.round(parseFloat(v || 0)).toLocaleString('en-IN');

const emptyCreateForm = {
  name: '',
  phone_number: '',
  email: '',
  gstin: '',
  date_of_birth: '',
  referral_code: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
};

const formatAddress = (address) => {
  if (!address) return '';
  if (typeof address === 'string') return address;

  return [
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(', '),
    address.postal_code,
    address.country,
  ].filter(Boolean).join('\n');
};

const CustomerSearchModal = ({ open, onClose, onSelectCustomer }) => {
  const dispatch = useDispatch();
  const showToast = useToast();
  const { currentOrderId, selectedCustomer, loyalty_redemption_amount } = useSelector((state) => state.order);
  const orgLoyaltyEnabled = useSelector((state) => state.auth.organization?.loyalty_enabled);

  const [tab, setTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [detailCustomerId, setDetailCustomerId] = useState(null);
  const [custFocusIndex, setCustFocusIndex] = useState(-1);

  const [triggerSearch, { data: searchData, isFetching: isSearching }] = useLazySearchCustomersQuery();
  const [createCustomer, { isLoading: isCreating }] = useCreateCustomerMutation();
  const [updateOrder, { isLoading: isAttaching }] = useUpdateOrderMutation();
  const [enrollLoyalty, { isLoading: isEnrolling }] = useEnrollLoyaltyMutation();
  const [activateCustomer, { isLoading: isReactivating }] = useActivateCustomerMutation();
  const [removeLoyalty] = useRemoveLoyaltyMutation();
  const { data: customerDetailData, isFetching: isDetailLoading } = useGetCustomerQuery(detailCustomerId, {
    skip: !detailCustomerId,
  });

  const searchTimer = useRef(null);

  const handleClose = () => {
    setSearchTerm('');
    setCreateForm(emptyCreateForm);
    setTab(0);
    setDetailCustomerId(null);
    setCustFocusIndex(-1);
    onClose();
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setCustFocusIndex(-1);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (val.trim().length >= 2) {
      searchTimer.current = setTimeout(() => triggerSearch({ search: val.trim() }), 400);
    }
  };

  const attachCustomer = async (customer) => {
    // A deactivated customer can't be billed (the order guard rejects inactive
    // references) — reactivate at the counter first, then attach.
    let cust = customer;
    if (cust.active === false) {
      try {
        const res = await activateCustomer(cust.id).unwrap();
        cust = res.data?.customer || res.data || { ...cust, active: true };
        showToast(`${cust.name} reactivated`, 'success');
      } catch (err) {
        showToast(err?.data?.error || 'Failed to reactivate customer', 'error');
        return;
      }
    }

    // Gift-voucher buyer picker: hand the customer back instead of attaching it to the bill.
    if (onSelectCustomer) {
      onSelectCustomer(cust);
      handleClose();
      return;
    }

    dispatch(setSelectedCustomer(cust));
    if (currentOrderId) {
      try {
        await updateOrder({ id: currentOrderId, customer_id: cust.id }).unwrap();
        // A held loyalty redemption belongs to the previous customer — clear it
        // so it never carries over to a different customer.
        if (parseFloat(loyalty_redemption_amount || 0) > 0) {
          const res = await removeLoyalty(currentOrderId).unwrap();
          dispatch(setCurrentOrder(res.data.order));
        }
      } catch (_) {}
    }
    handleClose();
  };

  const handleEnroll = async (id) => {
    try {
      await enrollLoyalty(id).unwrap();
      showToast('Enrolled in loyalty', 'success');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to enroll', 'error');
    }
  };

  const handleCreate = async () => {
    if (!createForm.name.trim() || !createForm.phone_number.trim()) {
      showToast('Name and phone number are required', 'warning');
      return;
    }

    const phoneNumber = createForm.phone_number.replace(/\D/g, '');
    if (phoneNumber.length !== 10) {
      showToast('Phone number must be 10 digits', 'warning');
      return;
    }

    const address_attributes = {
      line1: createForm.address_line1.trim(),
      line2: createForm.address_line2.trim(),
      city: createForm.city.trim(),
      state: createForm.state.trim(),
      postal_code: createForm.postal_code.trim(),
    };

    const payload = {
      name: createForm.name.trim(),
      phone_number: phoneNumber,
      email: createForm.email.trim(),
      gstin: createForm.gstin.trim().toUpperCase(),
    };

    if (createForm.date_of_birth) payload.date_of_birth = createForm.date_of_birth;
    if (createForm.referral_code.trim()) payload.referral_code = createForm.referral_code.trim().toUpperCase();

    if (Object.values(address_attributes).some(Boolean)) {
      payload.address_attributes = address_attributes;
    }

    try {
      const res = await createCustomer(payload).unwrap();
      const newCustomer = res.data?.customer || res.data;
      showToast(`Customer "${newCustomer.name}" created!`, 'success');
      await attachCustomer(newCustomer);
    } catch (err) {
      const errorDetails = err?.data?.details;
      const errorMessage = Array.isArray(errorDetails) && errorDetails.length > 0
        ? errorDetails.join(', ')
        : err?.data?.error || 'Failed to create customer';

      showToast(errorMessage, 'error');
    }
  };

  const customers = searchData?.data?.customers || (Array.isArray(searchData?.data) ? searchData.data : []);
  const detailCustomer = customerDetailData?.data?.customer || customerDetailData?.data || null;

  // Roving focus over search results: ArrowUp/Down move, Enter attaches the focused (or first) result
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      if (tab !== 0 || detailCustomerId || customers.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCustFocusIndex(i => (i + 1 >= customers.length ? 0 : i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCustFocusIndex(i => (i <= 0 ? customers.length - 1 : i - 1));
        return;
      }
      if (e.key === 'Enter' && !isAttaching) {
        e.preventDefault();
        const target = customers[custFocusIndex] ?? customers[0];
        attachCustomer(target);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, tab, detailCustomerId, customers, custFocusIndex, isAttaching, attachCustomer]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100vw', sm: 400 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <PersonAdd sx={{ color: '#6366F1', fontSize: 22 }} />
        <Typography fontWeight={800} fontSize="1rem" color="#1E293B" sx={{ flexGrow: 1 }}>
          Add Customer
        </Typography>
        {selectedCustomer && (
          <Chip
            size="small"
            label={selectedCustomer.name}
            sx={{ fontWeight: 700, fontSize: '0.68rem', bgcolor: '#EEF2FF', color: '#4F46E5', height: 22 }}
          />
        )}
        <IconButton size="small" onClick={handleClose} sx={{ color: '#64748B' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setCustFocusIndex(-1); }}
        sx={{
          px: 2.5, borderBottom: '1px solid #F1F5F9', flexShrink: 0,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', minHeight: 44, py: 0 },
        }}
      >
        <Tab label="Search Customer" />
        <Tab label="Create New" />
      </Tabs>

      {/* Body */}
      <Box sx={{
        flexGrow: 1, overflowY: 'auto', p: 2.5,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
      }}>
        {tab === 0 ? (
          detailCustomerId ? (
            <Stack spacing={2}>
              <Button
                startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
                onClick={() => setDetailCustomerId(null)}
                size="small"
                sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700, color: '#64748B', px: 0.5 }}
              >
                Back to results
              </Button>

              {isDetailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !detailCustomer ? (
                <Typography fontSize="0.8rem" color="#94A3B8">Customer details unavailable.</Typography>
              ) : (
                <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden' }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid #F1F5F9', display: 'flex', gap: 1.25, alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 800, width: 44, height: 44 }}>
                      {detailCustomer.name?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography fontSize="0.92rem" fontWeight={800} color="#1E293B" noWrap>
                          {detailCustomer.name || '—'}
                        </Typography>
                        {detailCustomer.active === false && (
                          <Chip
                            size="small"
                            label="Inactive"
                            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#FEF2F2', color: '#DC2626' }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Box>

                  <Stack spacing={1.1} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Phone sx={{ fontSize: 14, color: '#94A3B8' }} />
                      <Typography fontSize="0.78rem" color="#334155">{detailCustomer.phone_number || '—'}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Email sx={{ fontSize: 14, color: '#94A3B8' }} />
                      <Typography fontSize="0.78rem" color="#334155" sx={{ wordBreak: 'break-word' }}>
                        {detailCustomer.email || '—'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <ReceiptLong sx={{ fontSize: 14, color: '#94A3B8' }} />
                      <Typography fontSize="0.78rem" color="#334155" sx={{ wordBreak: 'break-word' }}>
                        {detailCustomer.gstin || '—'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <AccountBalanceWallet sx={{ fontSize: 14, color: '#16A34A' }} />
                      <Typography fontSize="0.78rem" color="#16A34A" fontWeight={700}>
                        Credit Balance: ₹{parseFloat(detailCustomer.credit_balance || 0).toFixed(2)}
                      </Typography>
                    </Stack>
                    {detailCustomer.loyalty && (
                      <Box sx={{ mt: 0.5, p: 1.25, borderRadius: '10px', bgcolor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Stack direction="row" spacing={0.6} alignItems="center">
                            <Stars sx={{ fontSize: 14, color: '#F59E0B' }} />
                            <Typography sx={{ fontSize: '0.64rem', fontWeight: 800, color: '#92400E', letterSpacing: '0.04em' }}>LOYALTY</Typography>
                          </Stack>
                          {detailCustomer.loyalty.tier?.name && (
                            <Chip
                              size="small"
                              label={detailCustomer.loyalty.tier.name}
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, borderRadius: '6px', bgcolor: `${detailCustomer.loyalty.tier.color_hex || '#94A3B8'}26`, color: '#1E293B' }}
                            />
                          )}
                        </Stack>
                        {detailCustomer.loyalty.enrolled ? (
                          <>
                            <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#1E293B' }}>
                              {fmtPts(detailCustomer.loyalty.points_balance)} pts · worth ₹{parseFloat(detailCustomer.loyalty.redeemable_amount || 0).toFixed(2)}
                            </Typography>
                            {detailCustomer.loyalty.referral_code && (
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                                <Typography sx={{ fontSize: '0.68rem', color: '#64748B' }}>Referral code to share:</Typography>
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#1E293B' }}>{detailCustomer.loyalty.referral_code}</Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => { navigator.clipboard?.writeText(detailCustomer.loyalty.referral_code); showToast('Code copied', 'success'); }}
                                  sx={{ p: 0.25, color: '#94A3B8', '&:hover': { color: '#4F46E5' } }}
                                >
                                  <ContentCopy sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Stack>
                            )}
                          </>
                        ) : (
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>Not enrolled</Typography>
                            <Button
                              size="small" variant="outlined" disableElevation
                              onClick={() => handleEnroll(detailCustomer.id)} disabled={isEnrolling}
                              sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', borderRadius: '6px', color: '#4F46E5', borderColor: '#C7D2FE', bgcolor: '#EEF2FF', '&:hover': { borderColor: '#A5B4FC', bgcolor: '#E0E7FF' } }}
                            >
                              {isEnrolling ? <CircularProgress size={12} /> : 'Enroll'}
                            </Button>
                          </Stack>
                        )}
                      </Box>
                    )}
                    {detailCustomer.address && (
                      <Stack direction="row" spacing={0.75} alignItems="flex-start">
                        <LocationOn sx={{ fontSize: 14, color: '#94A3B8', mt: 0.15 }} />
                        <Typography fontSize="0.76rem" color="#64748B" sx={{ whiteSpace: 'pre-wrap' }}>
                          {formatAddress(detailCustomer.address)}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  <Box sx={{ p: 2, borderTop: '1px solid #F1F5F9' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      disableElevation
                      onClick={() => attachCustomer(detailCustomer)}
                      disabled={isAttaching || isReactivating}
                      sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '8px', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}
                    >
                      {detailCustomer.active === false
                        ? (isReactivating ? 'Reactivating...' : 'Reactivate & Attach')
                        : (isAttaching ? 'Attaching...' : 'Attach Customer')}
                    </Button>
                  </Box>
                </Box>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              {/* Search input */}
              <TextField
                fullWidth autoFocus
                placeholder="Search by name or phone..."
                value={searchTerm}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#94A3B8', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: isSearching ? <CircularProgress size={16} /> : null,
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F8FAFC', fontSize: '0.9rem' } }}
              />

              {/* Results list */}
              {customers.length > 0 && (
                <List disablePadding sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
                  {customers.map((c, i) => {
                    const isFocused = i === custFocusIndex;
                    return (
                    <React.Fragment key={c.id}>
                      {i > 0 && <Divider />}
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => attachCustomer(c)}
                          disabled={isAttaching}
                          sx={{
                            py: 1.5, px: 2, gap: 1.5, '&:hover': { bgcolor: '#F8FAFC' },
                            bgcolor: isFocused ? '#EEF2FF' : 'transparent',
                            borderLeft: isFocused ? '3px solid #4F46E5' : '3px solid transparent',
                          }}
                        >
                          <Avatar sx={{
                            bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 800,
                            fontSize: '0.9rem', width: 40, height: 40, flexShrink: 0,
                          }}>
                            {c.name?.[0]?.toUpperCase()}
                          </Avatar>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography fontWeight={700} fontSize="0.88rem" color={isFocused ? '#4F46E5' : '#1E293B'} noWrap>
                              {c.name}
                            </Typography>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                              <Phone sx={{ fontSize: 12, color: '#94A3B8' }} />
                              <Typography fontSize="0.72rem" color="#64748B">{c.phone_number}</Typography>
                              {c.active === false && (
                                <Chip
                                  size="small"
                                  label="Inactive"
                                  sx={{
                                    height: 18, fontSize: '0.62rem', fontWeight: 700,
                                    bgcolor: '#FEF2F2', color: '#DC2626',
                                  }}
                                />
                              )}
                              {parseFloat(c.credit_balance || 0) > 0 && (
                                <Chip
                                  size="small"
                                  icon={<AccountBalanceWallet sx={{ fontSize: '10px !important' }} />}
                                  label={`₹${parseFloat(c.credit_balance).toFixed(0)}`}
                                  sx={{
                                    height: 18, fontSize: '0.62rem', fontWeight: 700,
                                    bgcolor: '#F0FDF4', color: '#16A34A',
                                    '& .MuiChip-icon': { color: '#16A34A' },
                                  }}
                                />
                              )}
                              {c.loyalty?.enrolled && parseFloat(c.loyalty.points_balance || 0) > 0 && (
                                <Chip
                                  size="small"
                                  icon={<Stars sx={{ fontSize: '10px !important' }} />}
                                  label={fmtPts(c.loyalty.points_balance)}
                                  sx={{
                                    height: 18, fontSize: '0.62rem', fontWeight: 700,
                                    bgcolor: '#FFFBEB', color: '#D97706',
                                    '& .MuiChip-icon': { color: '#F59E0B' },
                                  }}
                                />
                              )}
                            </Stack>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility sx={{ fontSize: '13px !important' }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetailCustomerId(c.id);
                            }}
                            sx={{
                              textTransform: 'none', fontWeight: 700, fontSize: '0.68rem',
                              borderRadius: '7px', minWidth: 0, px: { xs: 0.75, sm: 1 },
                              color: '#4F46E5', borderColor: '#C7D2FE', bgcolor: '#EEF2FF',
                              '&:hover': { borderColor: '#A5B4FC', bgcolor: '#E0E7FF' },
                              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } },
                            }}
                          >
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>View</Box>
                          </Button>
                        </ListItemButton>
                      </ListItem>
                    </React.Fragment>
                    );
                  })}
                </List>
              )}

              {/* Empty states */}
              {customers.length === 0 && searchTerm.length >= 2 && !isSearching && (
                <Box sx={{ textAlign: 'center', py: 4, color: '#94A3B8' }}>
                  <PersonAdd sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                  <Typography fontSize="0.85rem" fontWeight={600} color="#64748B">No customers found</Typography>
                  <Typography fontSize="0.75rem" color="#94A3B8" sx={{ mb: 1.5 }}>Try a different name or phone number</Typography>
                  <Button size="small" variant="outlined" onClick={() => setTab(1)}
                    sx={{ fontWeight: 700, textTransform: 'none', color: '#4F46E5', borderColor: '#C7D2FE', borderRadius: '8px', fontSize: '0.78rem' }}>
                    + Create New Customer
                  </Button>
                </Box>
              )}

              {searchTerm.length < 2 && (
                <Box sx={{ textAlign: 'center', py: 6, color: '#CBD5E1' }}>
                  <Search sx={{ fontSize: 40, mb: 1 }} />
                  <Typography fontSize="0.82rem" color="#94A3B8">Type at least 2 characters to search</Typography>
                </Box>
              )}
            </Stack>
          )
        ) : (
          <Stack spacing={2}>
            <TextField
              fullWidth label="Full Name *" size="small" autoFocus
              value={createForm.name}
              onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PersonAdd sx={{ color: '#94A3B8', fontSize: 18 }} /></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              fullWidth label="Phone Number *" size="small"
              value={createForm.phone_number}
              onChange={(e) => setCreateForm(f => ({ ...f, phone_number: e.target.value }))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Phone sx={{ color: '#94A3B8', fontSize: 18 }} /></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              fullWidth label="Email (optional)" size="small"
              value={createForm.email}
              onChange={(e) => setCreateForm(f => ({ ...f, email: e.target.value }))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Email sx={{ color: '#94A3B8', fontSize: 18 }} /></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              fullWidth label="GSTIN (optional)" size="small"
              value={createForm.gstin}
              onChange={(e) => setCreateForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><ReceiptLong sx={{ color: '#94A3B8', fontSize: 18 }} /></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            {orgLoyaltyEnabled && (
              <>
                <TextField
                  fullWidth label="Date of Birth (optional)" size="small" type="date"
                  value={createForm.date_of_birth}
                  onChange={(e) => setCreateForm(f => ({ ...f, date_of_birth: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Cake sx={{ color: '#94A3B8', fontSize: 18 }} /></InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                <TextField
                  fullWidth label="Referred by (referral code)" size="small"
                  value={createForm.referral_code}
                  onChange={(e) => setCreateForm(f => ({ ...f, referral_code: e.target.value.toUpperCase() }))}
                  helperText="Enter the code of the friend who referred them"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><CardGiftcard sx={{ color: '#94A3B8', fontSize: 18 }} /></InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, '& .MuiFormHelperText-root': { fontSize: '0.66rem', mx: 0.5 } }}
                />
              </>
            )}
            <TextField
              fullWidth label="Address Line 1 (optional)" size="small"
              value={createForm.address_line1}
              onChange={(e) => setCreateForm(f => ({ ...f, address_line1: e.target.value }))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LocationOn sx={{ color: '#94A3B8', fontSize: 18 }} /></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              fullWidth label="Address Line 2 (optional)" size="small"
              value={createForm.address_line2}
              onChange={(e) => setCreateForm(f => ({ ...f, address_line2: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth label="City (optional)" size="small"
                value={createForm.city}
                onChange={(e) => setCreateForm(f => ({ ...f, city: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
              <TextField
                fullWidth label="State (optional)" size="small"
                value={createForm.state}
                onChange={(e) => setCreateForm(f => ({ ...f, state: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Stack>
            <TextField
              fullWidth label="Postal Code (optional)" size="small"
              value={createForm.postal_code}
              onChange={(e) => setCreateForm(f => ({ ...f, postal_code: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isCreating) {
                  e.preventDefault();
                  handleCreate();
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Stack>
        )}
      </Box>

      {/* Footer — only shown on Create tab */}
      {tab === 1 && (
        <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #F1F5F9', flexShrink: 0, display: 'flex', gap: 1 }}>
          <Button
            fullWidth variant="outlined" onClick={handleClose}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', color: '#64748B', borderColor: '#E2E8F0' }}
          >
            Cancel
          </Button>
          <Button
            fullWidth variant="contained" disableElevation onClick={handleCreate} disabled={isCreating}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '8px', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}
          >
            {isCreating ? 'Creating...' : 'Create & Attach'}
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

export default CustomerSearchModal;
